import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMobileScreenButton, faCircleInfo } from "@fortawesome/free-solid-svg-icons";

/**
 * Nút "Mở trong app" — cứu cánh cho webview trong app khác (Zalo/Facebook),
 * nơi Universal Links (iOS) / App Links (Android) KHÔNG tự kích hoạt khi bấm link.
 *
 * Cách bắn theo nền tảng (12/08: webview iOS Zalo/FB chặn scheme lạ đặt bằng JS):
 * - Dùng thẻ <a href> THẬT thay vì gán window.location trong onClick — WKWebView/
 *   Android WebView duyệt navigation từ cú chạm anchor rộng rãi hơn JS navigation.
 * - Android: `intent://<path>#Intent;scheme=longhousee;package=...;end` — WebView
 *   Android chặn scheme lạ nhưng hiểu intent:// (kèm browser_fallback_url → /tai-app).
 * - iOS: `longhousee://<path>`.
 *
 * autoAttempt: TỰ thử mở app khi vào trang (1 lần/trang/phiên, chỉ mobile), bắn
 * bằng fireOpen (click anchor tổng hợp) nên webview iOS cũng có cửa qua.
 * Auto KHÔNG fallback /tai-app — không có cách phát hiện chắc chắn "chưa cài app",
 * đá tự động sẽ oan người muốn xem web. iOS còn có thể chặn vì thiếu cử chỉ người
 * dùng → auto luôn là "được thì tốt", nút bấm tay mới là đường đảm bảo.
 *
 * Bấm tay mà app không mở:
 * - Webview iOS (Zalo/FB): KHÔNG đá /tai-app — người dùng có thể đã cài app mà
 *   webview chặn; hiện hướng dẫn "••• → Mở trong Safari".
 * - Còn lại: fallback /tai-app sau 2,5s (hủy nếu trang bị ẩn = app đã mở).
 * Chỉ hiện trên mobile viewport (md:hidden) — desktop không có app để mở.
 */
// Guard THEO TỪNG TRANG: trước dùng 1 khoá chung cho cả site nên xem trang bài
// viết trước rồi sang trang thưởng là mất lượt auto (tưởng auto hỏng).
const autoKey = (path) => `open_in_app_auto:${path || window.location.pathname}`;
const ANDROID_PACKAGE = "com.lh.rewards";

const ua = () => (typeof navigator === "undefined" ? "" : navigator.userAgent || "");
const isIOS = () => /iPhone|iPad|iPod/.test(ua());
const isAndroid = () => /Android/.test(ua());
/** Trình duyệt nhúng trong app khác (Zalo, FB/Messenger, Instagram, TikTok, Line...) */
const isInAppBrowser = () =>
    /Zalo|FBAN|FBAV|FB_IAB|Instagram|Line\/|Messenger|TikTok|MicroMessenger/i.test(ua()) ||
    // Safari thật luôn có "Safari/" — webview iOS thì không
    (isIOS() && !/Safari\//.test(ua()));

function buildTarget(path) {
    const rawPath = String(path || window.location.pathname + window.location.search);
    const tail = rawPath.replace(/^\//, "");
    if (isAndroid()) {
        const fallback = encodeURIComponent(`${window.location.origin}/tai-app`);
        return `intent://${tail}#Intent;scheme=longhousee;package=${ANDROID_PACKAGE};S.browser_fallback_url=${fallback};end`;
    }
    return `longhousee://${tail}`;
}

/**
 * Bắn scheme bằng cú click anchor TỔNG HỢP thay vì gán window.location:
 * WKWebView (Zalo/FB iOS) nuốt navigation do JS gán location — kể cả khi có cú
 * chạm — nhưng cho qua navigation phát từ thẻ <a>. Đây là lý do nút bấm tay ăn.
 */
function fireOpen(target) {
    const a = document.createElement("a");
    a.href = target;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => a.remove(), 0);
}

export default function OpenInAppButton({
    path,
    autoAttempt = false,
    className = "",
    label = "Mở bài này trong App Longhousee",
}) {
    const [href, setHref] = useState("");
    const [showSafariHint, setShowSafariHint] = useState(false);

    // href tính ở client (phụ thuộc UA) — tránh lệch khi render lần đầu
    useEffect(() => {
        setHref(buildTarget(path));
    }, [path]);

    useEffect(() => {
        if (!autoAttempt) return undefined;
        // Desktop không có app → auto vô nghĩa. Webview iOS vẫn thử: bắn bằng
        // click anchor (fireOpen) nên có cửa qua, khác hẳn cách gán location cũ.
        if (window.matchMedia("(min-width: 768px)").matches) return undefined;
        try {
            const key = autoKey(path);
            if (sessionStorage.getItem(key)) return undefined;
            sessionStorage.setItem(key, "1");
        } catch {
            // storage bị chặn (private mode) → bỏ auto, tránh bắn lặp mỗi lần render
            return undefined;
        }
        // Chờ nhẹ cho trang render xong rồi mới bắn scheme (không chặn paint đầu)
        const t = setTimeout(() => {
            fireOpen(buildTarget(path));
        }, 700);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** Anchor tự điều hướng; chỉ cắm thêm phần "app không mở thì làm gì". */
    const handleClick = () => {
        const iosInApp = isIOS() && isInAppBrowser();

        let cancelled = false;
        const cancelFallback = () => {
            cancelled = true;
            cleanup();
        };
        const onVisibility = () => {
            if (document.hidden) cancelFallback();
        };
        const cleanup = () => {
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("pagehide", cancelFallback);
            window.removeEventListener("blur", cancelFallback);
        };
        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("pagehide", cancelFallback);
        window.addEventListener("blur", cancelFallback);

        setTimeout(() => {
            cleanup();
            if (cancelled || document.hidden) return;
            // Webview iOS: app có thể đã cài mà vẫn bị chặn → chỉ hướng dẫn, không đá đi
            if (iosInApp) setShowSafariHint(true);
            else window.location.href = "/tai-app";
        }, 2500);
    };

    return (
        <div className="md:hidden">
            <a
                href={href || "/tai-app"}
                onClick={handleClick}
                className={`w-full flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-brand-red to-brand-warm px-4 py-3 text-sm font-bold text-white shadow-lg shadow-orange-200/60 active:scale-[0.98] transition-all ${className}`}
            >
                <FontAwesomeIcon icon={faMobileScreenButton} className="text-base" />
                {label}
            </a>
            {showSafariHint && (
                <p className="mt-2 flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-700">
                    <FontAwesomeIcon icon={faCircleInfo} className="mt-0.5" />
                    <span>
                        Zalo/Facebook chặn mở app từ trình duyệt trong ứng dụng. Bấm nút <b>•••</b> góc trên
                        → <b>Mở trong Safari</b>, rồi bấm lại nút này. Chưa cài app?{" "}
                        <a href="/tai-app" className="font-bold underline">
                            Tải app
                        </a>
                    </span>
                </p>
            )}
        </div>
    );
}
